import { useId, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { EnumSelect } from '@/components/ui/enum-select'
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field'
import { FormDialog, FormDialogContent, FormDialogTrigger } from '@/components/ui/form-dialog'
import { apiClient } from '@/lib/api/client'
import type { HelmAirService } from '@/lib/api/types'
import { quickEditErrorMessage } from '@/lib/quick-edit'
import {
  AIR_SERVICES,
  SHIPMENT_CHOICES,
  shipmentChoice,
  shipmentFields,
  shipmentLabel,
  type ShipmentChoice,
} from '@/src/lib/air-service'

/**
 * How an inquiry would travel, on its own page: air (and how it flies: freight,
 * express, courier or charter), sea or road, with a way for admins to change
 * it as the customer decides.
 */
export function ShipmentEditor({
  inquiryId,
  reference,
  shippingMethod,
  airService,
  canEdit,
  onChanged,
}: {
  inquiryId: string
  reference: string
  shippingMethod: string | null | undefined
  airService: HelmAirService | null | undefined
  canEdit: boolean
  onChanged: () => void
}) {
  const id = useId()
  const current = shipmentChoice(shippingMethod, airService)
  const [open, setOpen] = useState(false)
  const [choice, setChoice] = useState<ShipmentChoice>(current)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // What was saved, shown until the page reloads.
  const [saved, setSaved] = useState<ShipmentChoice | null>(null)
  const shown = shipmentFields(saved ?? current)
  const chosenAir = AIR_SERVICES.find((service) => `AIR:${service.value}` === choice)

  function openChange(next: boolean) {
    if (saving) return
    if (next) {
      setChoice(saved ?? current)
      setError(null)
    }
    setOpen(next)
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    if (choice === (saved ?? current)) {
      setOpen(false)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await apiClient.updateInquiry(inquiryId, shipmentFields(choice))
      setOpen(false)
      setSaved(choice)
      onChanged()
    } catch (cause) {
      setError(quickEditErrorMessage(cause))
    } finally {
      setSaving(false)
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span data-testid="inquiry-shipment">
        {shipmentLabel(shown.shipping_method, shown.air_service)}
      </span>
      {canEdit && (
        <FormDialog open={open} onOpenChange={openChange}>
          <FormDialogTrigger asChild>
            <Button type="button" variant="outline" size="xs">
              Change
            </Button>
          </FormDialogTrigger>
          <FormDialogContent
            size="sm"
            title="Shipment"
            description={reference}
            submitLabel="Save"
            busy={saving}
            error={error}
            onSubmit={save}
          >
            <Field>
              <FieldLabel htmlFor={`${id}-shipment`}>How it travels</FieldLabel>
              <EnumSelect
                value={choice}
                onValueChange={(value) => setChoice(value as ShipmentChoice)}
                options={SHIPMENT_CHOICES}
                triggerProps={{ id: `${id}-shipment`, 'aria-describedby': `${id}-help` }}
              />
              <FieldDescription id={`${id}-help`}>
                {chosenAir?.description ?? 'Air, sea or road; for air, how it flies.'}
              </FieldDescription>
            </Field>
          </FormDialogContent>
        </FormDialog>
      )}
    </span>
  )
}
